/**
 * Paste a commercial proposal in as written.
 *
 * Owner, Sep 28 2026: "integrate this into the commercial jobs tab" — the email
 * he sends after walking a property: each property on its own line, then the
 * priced lines, some per visit, some one-time. Two steps (Oct 19 2026): paste
 * it, then see it the way the business will — a card per site with its
 * prices as tiles — before one press makes the account, a site per property
 * and a draft bid per site. The bid he sends to sign is that draft, from the
 * Bids page.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { X, ArrowLeft, Building2, MapPin, AlertTriangle, Sparkles } from 'lucide-react';
import { api } from '../services/api';

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US')}`;
const CADENCE: Record<string, string> = { one_time: 'one-time', every_2_weeks: 'every two weeks', weekly: 'weekly', quarterly: 'quarterly', monthly: 'monthly', yearly: 'yearly', per_visit: 'per visit' };
const PLACEHOLDER = 'Kami,\n\nAmerican Self Storage — 13750 W Hwy 29, Liberty Hill\nOne-time cleanup of the fence lines …… $550\nBiweekly mowing and vegetation spray …… $250 per visit\nQuarterly tree trimming …… $225 per visit\n\nStrip center — 12701 W Hwy 29, Liberty Hill\n…';

/** A site's lines as the tiles the business will read: a visit, a quarter, one time. */
function tilesOf(lines: any[]) {
  const sum = (f: (l: any) => boolean) => lines.filter(f).reduce((t, l) => t + (Number(l.price) || 0), 0);
  const visit = sum(l => l.unit === 'visit' && !['quarterly', 'monthly', 'yearly', 'one_time'].includes(String(l.cadence || '')));
  const quarter = sum(l => l.unit === 'visit' && l.cadence === 'quarterly');
  const once = sum(l => l.cadence === 'one_time' || (!l.unit && l.cadence !== 'quarterly'));
  const out: { label: string; value: string }[] = [];
  if (visit > 0) out.push({ label: 'a visit', value: money(visit) });
  if (quarter > 0) out.push({ label: 'a quarter', value: money(quarter) });
  if (once > 0) out.push({ label: 'one time', value: money(once) });
  return out;
}

export default function ProposalPaste({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [company, setCompany] = useState('');
  const [contact, setContact] = useState('');
  const [look, setLook] = useState<any>(null);

  const run = useMutation((dryRun: boolean) => api.post('/commercial/proposal', { text, dry_run: dryRun, company_name: company, contact_name: contact }).then(r => r.data), {
    onSuccess: (d: any) => {
      if (d.dry_run) {
        setLook(d);
        if (!contact && d.contact?.first_name) setContact(d.contact.first_name);
        if (!company && d.properties?.[0]?.name) setCompany(d.properties[0].name);
        return;
      }
      toast.success(d?.message || 'Added', { autoClose: 9000 });
      qc.invalidateQueries(['commercial']); qc.invalidateQueries('quotes');
      onClose();
      navigate(`/commercial/${d.customer_id}`);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not read that'); },
  });

  const props: any[] = look?.properties || [];
  const step: 1 | 2 = look && props.length > 0 ? 2 : 1;
  const totalOnce = props.reduce((t, p) => t + p.lines.filter((l: any) => l.cadence === 'one_time').reduce((x: number, l: any) => x + (Number(l.price) || 0), 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-3xl sm:rounded-3xl shadow-lift overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-brand-600 via-brand-400 to-brand-600" />
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            {step === 2 && (
              <button type="button" onClick={() => setLook(null)} aria-label="Back" className="h-9 w-9 -ml-2 flex items-center justify-center rounded-2xl text-gray-600 hover:bg-gray-100">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-wide text-brand-700">Step {step} of 2</p>
              <h2 className="font-black text-lg text-gray-900 leading-tight truncate">{step === 1 ? 'Paste the proposal' : 'This is what they will see'}</h2>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="h-9 w-9 flex items-center justify-center rounded-2xl text-gray-600 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {step === 1 ? (
            <>
              <p className="text-sm text-gray-600">The email as you sent it. Each property on its own line ("Name — Address"), then the priced lines. Per-visit lines keep their cadence; one-time lines are one-time. Nothing is made until you say so.</p>
              <textarea value={text} onChange={e => { setText(e.target.value); setLook(null); }} rows={11} autoFocus
                className="input w-full font-mono text-xs leading-relaxed" placeholder={PLACEHOLDER} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm"><span className="label-sm">Company — the account the bills go to</span>
                  <input value={company} onChange={e => setCompany(e.target.value)} className="input" placeholder="American Self Storage" maxLength={120} /></label>
                <label className="block text-sm"><span className="label-sm">Contact — who you walked it with</span>
                  <input value={contact} onChange={e => setContact(e.target.value)} className="input" placeholder="Kami" maxLength={120} /></label>
              </div>
              {look && props.length === 0 && (
                <p className="text-sm text-amber-800 font-semibold inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> No property line was found. Put each property on its own line as "Name — Address".</p>
              )}
              {look && (look.warnings || []).map((w: string, i: number) => <p key={i} className="text-sm text-amber-800">{w}</p>)}
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700">
                <span className="inline-flex items-center gap-1 font-bold text-gray-900"><Building2 className="h-4 w-4 text-gray-500" /> {company || props[0]?.name}</span>
                <span>{props.length} site{props.length === 1 ? '' : 's'}</span>
                {look.walked_on && <span>walked {look.walked_on}</span>}
                {look.contact?.decision_maker && <span>for {look.contact.decision_maker}</span>}
                {totalOnce > 0 && <span>{money(totalOnce)} one-time work in all</span>}
              </div>
              {(look.warnings || []).map((w: string, i: number) => (
                <p key={i} className="text-sm text-amber-800 inline-flex items-start gap-1"><AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /> {w}</p>
              ))}
              <ul className="space-y-3">
                {props.map((p: any, i: number) => {
                  const tiles = tilesOf(p.lines);
                  return (
                    <li key={i} className="rounded-2xl border border-brand-100 shadow-soft overflow-hidden">
                      <div className="px-4 pt-3 pb-2">
                        <p className="text-[11px] font-black uppercase tracking-wide text-brand-600">Site {i + 1} · one draft bid</p>
                        <p className="font-black text-gray-900 leading-tight">{p.name}</p>
                        <p className="text-sm text-gray-600 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[p.address, p.city].filter(Boolean).join(', ') || 'no address read'}</p>
                      </div>
                      {tiles.length > 0 && (
                        <div className={`px-4 py-3 bg-brand-600 text-black grid gap-3 ${tiles.length >= 3 ? 'grid-cols-3' : tiles.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                          {tiles.map(t => (
                            <div key={t.label} className="min-w-0">
                              <p className="text-xl sm:text-2xl font-black tracking-tight tabular-nums leading-none">{t.value}</p>
                              <p className="text-[12px] font-bold uppercase tracking-wide text-black/70 mt-1">{t.label}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="px-4 py-2.5 text-sm space-y-1">
                        {p.lines.map((l: any, j: number) => (
                          <p key={j} className="flex justify-between gap-3">
                            <span className="text-gray-800">{l.description}</span>
                            <span className="whitespace-nowrap font-semibold text-gray-900 tabular-nums">{money(l.price)}{l.unit === 'visit' ? ' / visit' : ''} <span className="font-normal text-gray-500">· {CADENCE[l.cadence] || l.cadence}</span></span>
                          </p>
                        ))}
                        {p.notes.map((n: string, j: number) => <p key={`n${j}`} className="text-gray-500 italic text-xs">{n}</p>)}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {(look.terms || []).length > 0 && (
                <div className="text-xs text-gray-600 space-y-0.5">{(look.terms || []).map((t: string, i: number) => <p key={`t${i}`}>{t}</p>)}</div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-gray-500">Nothing is sent. The bids stay drafts until you send them from Bids.</span>
          <div className="flex gap-2">
            {step === 1 ? (
              <button type="button" onClick={() => run.mutate(true)} disabled={run.isLoading || !text.trim()} className="btn-primary">
                {run.isLoading ? 'Reading…' : 'Read it'}
              </button>
            ) : (
              <button type="button" onClick={() => { if (!company.trim()) { toast.error('Give the company a name'); return; } run.mutate(false); }} disabled={run.isLoading} className="btn-primary">
                <Sparkles className="h-4 w-4" /> {run.isLoading ? 'Making…' : `Make the account, ${props.length} site${props.length === 1 ? '' : 's'} and ${props.length} draft bid${props.length === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
